import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['radioButton', 'checkbox', 'checkboxGroup'];

  connect() {
    // Initialize state when controller connects
    this.updateCheckboxStates();
  }

  handleRadioChange(event) {
    const selectedRadio = event.target;
    const optionKey = selectedRadio.value;

    if (selectedRadio.checked) {
      // First, uncheck all checkboxes for ALL radio options
      this.uncheckAllCheckboxesForAllOptions();

      // Then check all checkboxes for the currently selected option
      this.checkAllCheckboxesForOption(optionKey);
    } else {
      // When radio is deselected, uncheck all checkboxes for that option
      this.uncheckAllCheckboxesForOption(optionKey);
    }
  }

  handleCheckboxChange(event) {
    const checkbox = event.target;
    const optionKey = checkbox.dataset.optionKey;

    if (checkbox.checked) {
      // Check if we're switching to a different radio group
      const currentlySelectedRadio = this.getCurrentlySelectedRadio();

      if (
        currentlySelectedRadio &&
        currentlySelectedRadio.value !== optionKey
      ) {
        // We're switching to a different radio group, so clear everything first
        this.uncheckAllCheckboxesForAllOptions();
        this.uncheckAllRadios();

        // Check this checkbox again (since we unchecked it above)
        checkbox.checked = true;
      }

      // Check the associated radio button
      this.checkRadioForOptionOnly(optionKey);
    } else {
      // If checkbox is being unchecked, check if any other checkboxes for this option are still checked
      if (!this.hasAnyCheckedCheckboxesForOption(optionKey)) {
        this.uncheckRadioForOption(optionKey);
      }
    }
  }

  checkAllCheckboxesForOption(optionKey) {
    const checkboxGroup = this.findCheckboxGroupForOption(optionKey);
    if (checkboxGroup) {
      const checkboxes = checkboxGroup.querySelectorAll(
        'input[type="checkbox"]',
      );
      checkboxes.forEach((checkbox) => {
        checkbox.checked = true;
      });
    }
  }

  uncheckAllCheckboxesForOption(optionKey) {
    const checkboxGroup = this.findCheckboxGroupForOption(optionKey);
    if (checkboxGroup) {
      const checkboxes = checkboxGroup.querySelectorAll(
        'input[type="checkbox"]',
      );
      checkboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });
    }
  }

  uncheckAllCheckboxesForAllOptions() {
    // Uncheck all checkboxes for all radio button options
    this.checkboxGroupTargets.forEach((checkboxGroup) => {
      const checkboxes = checkboxGroup.querySelectorAll(
        'input[type="checkbox"]',
      );
      checkboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });
    });
  }

  hasAnyCheckedCheckboxesForOption(optionKey) {
    const checkboxGroup = this.findCheckboxGroupForOption(optionKey);
    if (checkboxGroup) {
      const checkboxes = checkboxGroup.querySelectorAll(
        'input[type="checkbox"]',
      );
      return Array.from(checkboxes).some((checkbox) => checkbox.checked);
    }
    return false;
  }

  checkRadioForOption(optionKey) {
    const radio = this.findRadioForOption(optionKey);
    if (radio && !radio.checked) {
      // First, uncheck all checkboxes for ALL radio options
      this.uncheckAllCheckboxesForAllOptions();

      // Then check the radio button
      radio.checked = true;

      // Finally, check all checkboxes for this option
      this.checkAllCheckboxesForOption(optionKey);
    }
  }

  checkRadioForOptionOnly(optionKey) {
    const radio = this.findRadioForOption(optionKey);
    if (radio) {
      radio.checked = true;
    }
  }

  uncheckAllRadios() {
    this.radioButtonTargets.forEach((radio) => {
      radio.checked = false;
    });
  }

  uncheckRadioForOption(optionKey) {
    const radio = this.findRadioForOption(optionKey);
    if (radio) {
      radio.checked = false;
    }
  }

  getCurrentlySelectedRadio() {
    return this.radioButtonTargets.find((radio) => radio.checked);
  }

  findCheckboxGroupForOption(optionKey) {
    return this.checkboxGroupTargets.find(
      (group) => group.dataset.optionKey === optionKey,
    );
  }

  findRadioForOption(optionKey) {
    return this.radioButtonTargets.find((radio) => radio.value === optionKey);
  }

  updateCheckboxStates() {
    // Update checkbox states based on current radio selections
    this.radioButtonTargets.forEach((radio) => {
      if (radio.checked) {
        const optionKey = radio.value;
        // If radio is checked but no checkboxes are checked, check all checkboxes
        if (!this.hasAnyCheckedCheckboxesForOption(optionKey)) {
          this.checkAllCheckboxesForOption(optionKey);
        }
      }
    });
  }
}
